import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Building, Calendar, Search, Download, Filter, ChevronDown, X } from 'lucide-react';
import './SSTFormatDetails.css';
import { getSstFormatByClientId } from '../../api/sstFormatapi';
import ExportTypeModal from './ExportTypeModal';
import {
  generateMISCSVContent,
  generateSummaryCSVContent,
  triggerCSVDownload,
  mapSSTDetailsData,
  generateMISRows,
  generateSummaryRows,
  triggerExcelDownload,
} from './sstExportUtils';
import { getExportFileName } from '../../utils/fileExportNaming';
import { useToast } from '../shared/Toast';
import CustomSelect from '../shared/CustomSelect';

const COLUMNS = [
  { key: 'chk', label: '', width: '40px', align: 'center' },
  { key: 'slNo', label: 'Sl No', flex: 0.5, align: 'center' },
  { key: 'clientName', label: 'Client Name', flex: 1, align: 'center' },
  { key: 'date', label: 'Date', flex: 1, align: 'center' },
  { key: 'tripNo', label: 'Trip No', flex: 0.8, align: 'center' },
  { key: 'vehNo', label: 'Veh No', flex: 1, align: 'center' },
  { key: 'pickupDrop', label: 'Pickup/Drop', flex: 1, align: 'center' },
  { key: 'time', label: 'Time', flex: 0.8, align: 'center' },
  { key: 'vehicleType', label: 'Vehicle Type', flex: 1, align: 'center' },
  { key: 'rosteredEmp', label: 'Rostered Emp', flex: 1.2, align: 'center' },
  { key: 'boardedEmp', label: 'Boarded Emp', flex: 1, align: 'center' },
  { key: 'location', label: 'Location', flex: 1.5, align: 'center' },
  { key: 'escort', label: 'Escort', flex: 0.7, align: 'center' },
  { key: 'kms', label: 'KMs', flex: 0.6, align: 'center' },
  { key: 'slab', label: 'Slab', flex: 0.5, align: 'center' },
  { key: 'tripAmount', label: 'Trip Amount', flex: 1, align: 'center' },
  { key: 'escortAmount', label: 'Escort Amount', flex: 1, align: 'center' },
  { key: 'totalAmount', label: 'Total Amount', flex: 1, align: 'center' },
  { key: 'delayPenalties', label: 'Delay Penalties', flex: 1, align: 'center' },
  { key: 'remarks', label: 'Remarks', flex: 1, align: 'center' },
];

const getAlignStyle = (align) => {
  switch (align) {
    case 'left': return { textAlign: 'left', justifyContent: 'flex-start' };
    case 'right': return { textAlign: 'right', justifyContent: 'flex-end' };
    case 'center':
    default: return { textAlign: 'center', justifyContent: 'center' };
  }
};

const formatCellValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return 'Null';
  }
  return value;
};

const TableHeader = ({ onSelectAll, isAllSelected }) => (
  <div style={{ display: 'flex', backgroundColor: '#EEF8F3', color: '#333', padding: '14px 16px', fontSize: '13px', fontWeight: 600, borderBottom: '1px solid #E8E8E8', minWidth: '2000px', position: 'sticky', top: 0, zIndex: 2 }}>
    {COLUMNS.map(col =>
      col.key === 'chk'
        ? (
          <div key={col.key} style={{ width: col.width, display: 'flex', alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={isAllSelected}
              onChange={(e) => onSelectAll && onSelectAll(e.target.checked)}
              style={{ width: '16px', height: '16px', cursor: 'pointer' }}
            />
          </div>
        )
        : <div key={col.key} style={{ flex: col.flex, ...getAlignStyle(col.align), padding: '0 8px' }}>{col.label}</div>
    )}
  </div>
);

const TableRow = ({ data, isSelected, onToggleSelect }) => (
  <div className="sst-table-row" style={isSelected ? { backgroundColor: '#F0FBF5' } : {}}>
    {COLUMNS.map(col =>
      col.key === 'chk'
        ? (
          <div key={col.key} style={{ width: col.width, display: 'flex', alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => onToggleSelect && onToggleSelect(data.id || data.slNo)}
              style={{ width: '16px', height: '16px', cursor: 'pointer' }}
            />
          </div>
        )
        : <div key={col.key} style={{ flex: col.flex, ...getAlignStyle(col.align), color: '#333', padding: '0 8px' }}>{formatCellValue(data[col.key])}</div>
    )}
  </div>
);

/* ─── Details Filter Panel ─────────────────────────────────────────────── */
const DetailsFilterPanel = ({ isOpen, onClose, onApply, currentFilters, vehicleTypes = [] }) => {
  const [vehicleType, setVehicleType] = useState(currentFilters?.vehicleType || '');
  const [pickupDrop, setPickupDrop] = useState(currentFilters?.pickupDrop || '');
  const [escort, setEscort] = useState(currentFilters?.escort || '');
  const [startDate, setStartDate] = useState(currentFilters?.startDate || '');
  const [endDate, setEndDate] = useState(currentFilters?.endDate || '');

  useEffect(() => {
    setVehicleType(currentFilters?.vehicleType || '');
    setPickupDrop(currentFilters?.pickupDrop || '');
    setEscort(currentFilters?.escort || '');
    setStartDate(currentFilters?.startDate || '');
    setEndDate(currentFilters?.endDate || '');
  }, [currentFilters, isOpen]);

  if (!isOpen) return null;

  const handleApply = () => {
    onApply({ vehicleType, pickupDrop, escort, startDate, endDate });
    onClose();
  };

  const handleReset = () => {
    setVehicleType('');
    setPickupDrop('');
    setEscort('');
    setStartDate('');
    setEndDate('');
    onApply({ vehicleType: '', pickupDrop: '', escort: '', startDate: '', endDate: '' });
    onClose();
  };

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1000,
        }}
      />
      <div
        style={{
          position: 'absolute',
          top: '60px',
          right: '20px',
          width: '290px',
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          zIndex: 1001,
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            marginBottom: '16px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
            <h2
              style={{
                margin: 0,
                fontSize: '18px',
                fontWeight: 600,
                color: '#20B967',
              }}
            >
              Filters
            </h2>
            <span style={{ fontSize: '13px', color: '#555' }}>Filter Records</span>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              color: '#888',
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Vehicle Type */}
        {vehicleTypes.length > 0 && (
          <div style={{ marginBottom: '14px' }}>
            <label
              style={{
                display: 'block',
                fontSize: '11px',
                fontWeight: 600,
                color: '#333',
                marginBottom: '6px',
                textTransform: 'uppercase',
              }}
            >
              Vehicle Type
            </label>
            <CustomSelect
              value={vehicleType}
              onChange={(val) => setVehicleType(val)}
              options={[
                { value: '', label: 'All Vehicle Types' },
                ...vehicleTypes.map((vt) => ({ value: vt, label: vt }))
              ]}
              placeholder="All Vehicle Types"
              width="100%"
              size="sm"
            />
          </div>
        )}

        {/* Pickup / Drop */}
        <div style={{ marginBottom: '14px' }}>
          <label
            style={{
              display: 'block',
              fontSize: '11px',
              fontWeight: 600,
              color: '#333',
              marginBottom: '6px',
              textTransform: 'uppercase',
            }}
          >
            Pickup / Drop
          </label>
          <CustomSelect
            value={pickupDrop}
            onChange={(val) => setPickupDrop(val)}
            options={[
              { value: '', label: 'All' },
              { value: 'Pickup', label: 'Pickup' },
              { value: 'Drop', label: 'Drop' },
            ]}
            placeholder="All"
            width="100%"
            size="sm"
          />
        </div>

        {/* Escort */}
        <div style={{ marginBottom: '14px' }}>
          <label
            style={{
              display: 'block',
              fontSize: '11px',
              fontWeight: 600,
              color: '#333',
              marginBottom: '6px',
              textTransform: 'uppercase',
            }}
          >
            Escort
          </label>
          <CustomSelect
            value={escort}
            onChange={(val) => setEscort(val)}
            options={[
              { value: '', label: 'All' },
              { value: 'Yes', label: 'Yes (With Escort)' },
              { value: 'No', label: 'No (Without Escort)' },
            ]}
            placeholder="All"
            width="100%"
            size="sm"
          />
        </div>

        {/* Date Range */}
        <div style={{ marginBottom: '16px' }}>
          <label
            style={{
              display: 'block',
              fontSize: '11px',
              fontWeight: 600,
              color: '#333',
              marginBottom: '6px',
              textTransform: 'uppercase',
            }}
          >
            Date Range
          </label>
          <div style={{ display: 'flex', gap: '8px' }}>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={{
                flex: 1,
                padding: '6px 8px',
                border: '1px solid #C5C5C5',
                borderRadius: '6px',
                fontSize: '12px',
              }}
              placeholder="From"
            />
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={{
                flex: 1,
                padding: '6px 8px',
                border: '1px solid #C5C5C5',
                borderRadius: '6px',
                fontSize: '12px',
              }}
              placeholder="To"
            />
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
          <button
            onClick={handleReset}
            style={{
              flex: 1,
              padding: '8px',
              border: '1px solid #C5C5C5',
              borderRadius: '6px',
              backgroundColor: 'white',
              color: '#555',
              fontSize: '13px',
              cursor: 'pointer',
            }}
          >
            Reset
          </button>
          <button
            onClick={handleApply}
            style={{
              flex: 2,
              padding: '8px',
              border: 'none',
              borderRadius: '6px',
              backgroundColor: '#20B967',
              color: 'white',
              fontSize: '13px',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Apply
          </button>
        </div>
      </div>
    </>
  );
};

const SSTFormatDetails = ({ company = 'Abilio IT', date = '10Jan, 2026', formatId, onBack }) => {
  const toast = useToast();
  const [hoverDownload, setHoverDownload] = useState(false);
  const [hoverFilter, setHoverFilter] = useState(false);
  const [detailsData, setDetailsData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState({
    vehicleType: '',
    pickupDrop: '',
    escort: '',
    startDate: '',
    endDate: '',
  });
  const [selectedRowIds, setSelectedRowIds] = useState(new Set());

  // Extract unique vehicle types for filtering
  const uniqueVehicleTypes = useMemo(() => {
    const types = detailsData
      .map((row) => row.vehicleType)
      .filter((t) => t && String(t).trim() !== '' && String(t).toLowerCase() !== 'null');
    return [...new Set(types)];
  }, [detailsData]);

  // Filtered rows based on search term & filter panel criteria
  const filteredData = useMemo(() => {
    return detailsData.filter((row) => {
      // Search term filter
      if (searchTerm.trim()) {
        const term = searchTerm.toLowerCase().trim();
        const matchesSearch = Object.entries(row).some(([key, val]) => {
          if (key === 'id' || key === 'chk' || key === 'rawClientData') return false;
          if (val === null || val === undefined) return false;
          return String(val).toLowerCase().includes(term);
        });
        if (!matchesSearch) return false;
      }

      // Vehicle Type filter
      if (filters.vehicleType && row.vehicleType) {
        if (String(row.vehicleType).toLowerCase() !== String(filters.vehicleType).toLowerCase()) {
          return false;
        }
      }

      // Pickup/Drop filter
      if (filters.pickupDrop && row.pickupDrop) {
        if (String(row.pickupDrop).toLowerCase() !== String(filters.pickupDrop).toLowerCase()) {
          return false;
        }
      }

      // Escort filter
      if (filters.escort) {
        const isEscortYes =
          String(row.escort).toLowerCase() === 'yes' ||
          row.escort === true ||
          String(row.escort) === '1';
        if (filters.escort === 'Yes' && !isEscortYes) return false;
        if (filters.escort === 'No' && isEscortYes) return false;
      }

      // Date range filter
      if (filters.startDate || filters.endDate) {
        const rowDateStr = row.date || row.Date;
        if (rowDateStr) {
          const rowDateObj = new Date(rowDateStr);
          if (!isNaN(rowDateObj.getTime())) {
            if (filters.startDate) {
              const startObj = new Date(filters.startDate);
              startObj.setHours(0, 0, 0, 0);
              if (rowDateObj < startObj) return false;
            }
            if (filters.endDate) {
              const endObj = new Date(filters.endDate);
              endObj.setHours(23, 59, 59, 999);
              if (rowDateObj > endObj) return false;
            }
          }
        }
      }

      return true;
    });
  }, [detailsData, searchTerm, filters]);

  const isAllSelected =
    filteredData.length > 0 &&
    filteredData.every((r) => selectedRowIds.has(r.id || r.slNo));

  const handleSelectAll = (checked) => {
    if (checked) {
      const allIds = new Set(filteredData.map((r) => r.id || r.slNo));
      setSelectedRowIds(allIds);
    } else {
      setSelectedRowIds(new Set());
    }
  };

  const handleToggleSelect = (rowId) => {
    const next = new Set(selectedRowIds);
    if (next.has(rowId)) {
      next.delete(rowId);
    } else {
      next.add(rowId);
    }
    setSelectedRowIds(next);
  };

  const processDownload = (exportType, fileFormat = 'xlsx') => {
    let dataToExport = detailsData;
    if (selectedRowIds.size > 0) {
      dataToExport = detailsData.filter((r) => selectedRowIds.has(r.id || r.slNo));
    } else if (searchTerm.trim() || Object.values(filters).some(Boolean)) {
      dataToExport = filteredData;
    }

    if (dataToExport.length === 0) {
      toast('No data to download', 'info');
      return;
    }

    const fileName = getExportFileName({
      reportName: exportType,
      data: dataToExport,
      fallbackDate: date,
      ext: fileFormat,
    });

    if (exportType === 'Summary') {
      if (fileFormat === 'xlsx') {
        const tableData = generateSummaryRows(dataToExport, date);
        triggerExcelDownload(tableData, fileName, 'Summary');
      } else {
        const csvContent = generateSummaryCSVContent(dataToExport, date);
        triggerCSVDownload(csvContent, fileName);
      }
    } else {
      if (fileFormat === 'xlsx') {
        const tableData = generateMISRows(dataToExport);
        triggerExcelDownload(tableData, fileName, 'MIS');
      } else {
        const csvContent = generateMISCSVContent(dataToExport);
        triggerCSVDownload(csvContent, fileName);
      }
    }
  };

  const fetchSstFormatDetails = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await getSstFormatByClientId(formatId);
      const sstData = response.data || response || [];
      const mappedData = mapSSTDetailsData(sstData);
      setDetailsData(mappedData);
    } catch (err) {
      setError('Failed to load trip sheet details');
    } finally {
      setIsLoading(false);
    }
  }, [formatId]);

  useEffect(() => {
    if (formatId) {
      fetchSstFormatDetails();
    }
  }, [formatId, fetchSstFormatDetails]);

  const hasActiveFilters = Object.values(filters).some(Boolean);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 80px - 64px)',
        fontFamily: "'Inter', sans-serif",
        position: 'relative',
      }}
    >
      {/* Top Bar */}
      <div
        style={{
          flexShrink: 0,
          backgroundColor: '#EEF8F3',
          borderRadius: '8px',
          padding: '12px 20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '8px',
          position: 'relative',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
          <button
            onClick={onBack}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '6px',
              transition: 'background-color 0.2s',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#dcf3e6')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
          >
            <ChevronDown size={22} color="#20B967" style={{ transform: 'rotate(90deg)' }} />
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Building size={22} color="#20B967" />
            <span style={{ fontSize: '20px', color: '#333', fontWeight: 600 }}>{company}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Calendar size={22} color="#20B967" />
            <span style={{ fontSize: '18px', color: '#333', fontWeight: 500 }}>{date}</span>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {/* Search Bar */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              backgroundColor: '#F5F1FA',
              borderRadius: '20px',
              padding: '10px 16px',
              width: '260px',
            }}
          >
            <input
              type="text"
              placeholder="Search Here"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                border: 'none',
                background: 'transparent',
                outline: 'none',
                flexGrow: 1,
                fontFamily: 'inherit',
                color: '#333',
                fontSize: '14px',
              }}
            />
            {searchTerm ? (
              <X
                size={18}
                color="#777"
                style={{ cursor: 'pointer', marginLeft: '4px' }}
                onClick={() => setSearchTerm('')}
                title="Clear search"
              />
            ) : (
              <Search size={18} color="#555" />
            )}
          </div>

          {/* Filters Button */}
          <button
            onClick={() => setIsFilterOpen(!isFilterOpen)}
            onMouseEnter={() => setHoverFilter(true)}
            onMouseLeave={() => setHoverFilter(false)}
            style={{
              backgroundColor: hoverFilter ? '#1c9d57' : '#20B967',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              padding: '10px 16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 500,
              transition: 'background-color 0.2s',
              position: 'relative',
            }}
          >
            Filters <Filter size={16} />
            {hasActiveFilters && (
              <span
                style={{
                  width: '8px',
                  height: '8px',
                  backgroundColor: '#FCD34D',
                  borderRadius: '50%',
                  display: 'inline-block',
                }}
              />
            )}
          </button>

          {/* Download Button */}
          <button
            onClick={() => setExportModalOpen(true)}
            onMouseEnter={() => setHoverDownload(true)}
            onMouseLeave={() => setHoverDownload(false)}
            style={{
              backgroundColor: hoverDownload ? '#f0fcf5' : 'white',
              color: '#20B967',
              border: '1px solid #20B967',
              borderRadius: '6px',
              padding: '10px 16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 500,
              transition: 'background-color 0.2s',
            }}
          >
            Download <Download size={16} />
          </button>
        </div>

        {/* Filter Popup Panel */}
        <DetailsFilterPanel
          isOpen={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          onApply={setFilters}
          currentFilters={filters}
          vehicleTypes={uniqueVehicleTypes}
        />
      </div>

      {/* Search / Filter Status Banner */}
      {(searchTerm.trim() || hasActiveFilters || selectedRowIds.size > 0) && (
        <div
          style={{
            padding: '8px 16px',
            backgroundColor: '#f0f9ff',
            border: '1px solid #bae6fd',
            borderRadius: '6px',
            fontSize: '14px',
            color: '#0369a1',
            marginBottom: '8px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span>
            Found <strong>{filteredData.length}</strong> record
            {filteredData.length !== 1 ? 's' : ''}
            {searchTerm.trim() ? ` matching "${searchTerm}"` : ''}
            {hasActiveFilters ? ' (filters applied)' : ''}
            {selectedRowIds.size > 0 ? ` • ${selectedRowIds.size} selected` : ''}
          </span>
          <button
            onClick={() => {
              setSearchTerm('');
              setFilters({ vehicleType: '', pickupDrop: '', escort: '', startDate: '', endDate: '' });
              setSelectedRowIds(new Set());
            }}
            style={{
              background: 'none',
              border: 'none',
              color: '#0369a1',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 500,
              textDecoration: 'underline',
            }}
          >
            Reset
          </button>
        </div>
      )}

      {/* Table */}
      <div
        style={{
          flex: 1,
          backgroundColor: 'white',
          borderRadius: '8px',
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {isLoading && (
          <div style={{ padding: '40px', textAlign: 'center', color: '#666', fontSize: '14px' }}>
            Loading trip sheet details...
          </div>
        )}
        {error && (
          <div
            style={{
              padding: '12px 16px',
              backgroundColor: '#FEE2E2',
              color: '#DC2626',
              borderRadius: '6px',
              margin: '16px',
              fontSize: '14px',
            }}
          >
            {error}
          </div>
        )}
        {!isLoading && !error && (
          <div
            className="custom-scrollbar hide-vertical-scrollbar"
            style={{ overflow: 'auto', flex: 1, minHeight: 0, paddingBottom: '16px' }}
          >
            <TableHeader
              onSelectAll={handleSelectAll}
              isAllSelected={isAllSelected}
            />
            {filteredData.length > 0 ? (
              filteredData.map((row, index) => (
                <TableRow
                  key={row.id || index}
                  data={row}
                  isSelected={selectedRowIds.has(row.id || row.slNo)}
                  onToggleSelect={handleToggleSelect}
                />
              ))
            ) : (
              <div style={{ padding: '40px', textAlign: 'center', color: '#666', fontSize: '14px' }}>
                {searchTerm.trim() || hasActiveFilters
                  ? `No trip sheet details found matching the criteria`
                  : 'No trip sheet details available'}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Export Type Modal */}
      <ExportTypeModal
        isOpen={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        onConfirm={processDownload}
        companyName={company}
      />
    </div>
  );
};

export default SSTFormatDetails;
